<template>
  <BaseLayout :menuLinks="sideMenuLinks">
    <template v-slot>
      <!-- Skip to main content link for screen readers -->
      <a href="#main-content" class="skip-link">
        Skip to main content
      </a>
      <!-- Modern Breadcrumb Navigation -->
      <ModernBreadcrumb :items="breadcrumbItems" />

      <!-- Enhanced Empty State -->
      <main id="main-content" v-if="sources.length === 0 && !loadingFeeds" 
            class="text-center p-6 max-w-3xl mx-auto" 
            role="main" 
            aria-labelledby="empty-state-title"
            aria-describedby="empty-state-description">
        <!-- Clean Visual Icon -->
        <div class="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-2xl flex items-center justify-content-center relative overflow-hidden sparkle-container animate-pulse-gentle">
          <span class="text-6xl sparkle-icon">✨</span>
        </div>
        
        <!-- Engaging Copy -->
        <h2 id="empty-state-title" class="text-2xl font-bold text-slate-800 mb-3">
          Ready to connect your first feed ? 🚀
        </h2>
        <p class="text-slate-600 text-lg max-w-xl mx-auto mb-6">
          Transform your data into actionable insights.<br>
          <span class="text-blue-600 font-medium">Simple, fast and secure</span> - let's create your first feed!
        </p>
        
        <!-- Call to Action -->
        <Button
          label="Create Your First Feed"
          icon="pi pi-plus"
          size="large"
          severity="success"
          class="px-8 py-3 text-lg font-semibold cta-button"
          @click="navigateToSetup"/>
        
        <!-- Quick Stats Preview -->
        <div class="flex justify-content-center gap-6 mt-6 pt-4 border-t-1 border-gray-200">
          <div class="text-center">
            <div class="text-lg font-bold text-blue-600">2 min</div>
            <div class="text-sm text-gray-500">Setup time</div>
          </div>
          <div class="text-center">
            <div class="text-lg font-bold text-purple-600">5+</div>
            <div class="text-sm text-gray-500">Source types</div>
          </div>
          <div class="text-center">
            <div class="text-lg font-bold text-green-600">100%</div>
            <div class="text-sm text-gray-500">Secure</div>
          </div>
        </div>
      </main>

      <!-- Enhanced Loading State -->
      <main id="main-content" v-else-if="loadingFeeds" 
            class="text-center p-6" 
            role="main" 
            aria-live="polite" 
            aria-busy="true"
            aria-labelledby="loading-title">
        <div class="bg-blue-100 text-blue-500 p-4 border-circle inline-flex mb-3 pi pi-spinner pi-spin">
        </div>
        <div id="loading-title" class="text-gray-600 font-semibold">Loading your feeds...</div>
        <div class="text-gray-500 text-sm mt-1">Please wait while we fetch your data</div>
      </main>

      <!-- Content when there are feeds -->
      <main id="main-content" v-else class="w-full" role="main">

        <!-- Standard Header -->
        <StandardHeader
          title="My Feeds"
          subtitle="Connect and manage your data sources"
          icon="pi pi-list"
          :actions="headerActions"
        />

        <!-- More menu (separate component) -->
        <Menu
          ref="moreMenu"
          :model="moreMenuItems"
          :popup="true"
        />

        <!-- Enhanced Main Content Card -->
        <Card class="shadow-modern border-round-2xl overflow-hidden w-full main-content-card">
        <template #title>
          <div class="flex align-items-center justify-content-between p-3 mb-3 w-full stats-header">
            <div class="flex align-items-center">
              <div class="bg-gray-100 text-gray-600 p-2 border-round-lg mr-3 pi pi-chart-bar">
              </div>
              <div>
                <h2 class="text-lg font-semibold text-slate-800 m-0">Data Overview</h2>
                <p class="text-slate-600 m-0 text-sm font-medium">{{ sources.length }} connected sources</p>
              </div>
            </div>
            <!-- Compact Stats in Header - Responsive Stack -->
            <div class="flex flex-column sm:flex-row gap-1 sm:gap-2 align-items-start sm:align-items-center">
              <div class="bg-blue-50 text-blue-600 px-2 py-1 border-round text-xs font-medium border-l-2 border-blue-400 flex align-items-center gap-1 w-full sm:w-auto"
                   v-tooltip.left="'Total connected feeds'">
                <i class="pi pi-database text-xs"></i>
                <span>{{ sources.length }}</span>
              </div>
              <div class="bg-green-50 text-green-600 px-2 py-1 border-round text-xs font-medium border-l-2 border-green-400 flex align-items-center gap-1 w-full sm:w-auto"
                   v-tooltip.left="'Active feeds'">
                <i class="pi pi-check-circle text-xs"></i>
                <span>{{ activeFeeds }}</span>
              </div>
              <div class="bg-orange-50 text-orange-600 px-2 py-1 border-round text-xs font-medium border-l-2 border-orange-400 flex align-items-center gap-1 w-full sm:w-auto"
                   v-tooltip.left="'Protected feeds (with authentication)'">
                <i class="pi pi-shield text-xs"></i>
                <span>{{ authenticatedFeeds }}</span>
              </div>
              <div class="bg-purple-50 text-purple-600 px-2 py-1 border-round text-xs font-medium border-l-2 border-purple-400 flex align-items-center gap-1 w-full sm:w-auto"
                   v-tooltip.left="'Web API feeds'">
                <i class="pi pi-server text-xs"></i>
                <span>{{ restApiFeeds }}</span>
              </div>
            </div>
          </div>
        </template>

        <template #content>
          <!-- Compact Search and Filter Controls -->
          <div class="datatable-header flex flex-column sm:flex-row justify-content-between align-items-center bg-white border-1 border-gray-200 p-2 border-round-lg mb-2 controls-section controls-minimal">
            <div class="flex gap-2 mb-2 sm:mb-0">
              <Button
                type="button"
                icon="pi pi-filter-slash"
                size="small"
                severity="secondary"
                text
                @click="clearFeedsFilter()"
                aria-label="Clear all filters"
                v-tooltip="'Clear all filters'"/>
              <Button
                type="button"
                icon="pi pi-trash"
                size="small"
                severity="danger"
                outlined
                class="modern-button"
                :class="{'opacity-50 cursor-not-allowed': !selectedFeeds || selectedFeeds.length === 0}"
                :disabled="!selectedFeeds || selectedFeeds.length === 0"
                @click="confirmBatchDelete"
                v-tooltip="selectedFeeds?.length ? `Delete ${selectedFeeds.length} selected feeds` : 'Select feeds to delete'"/>
              
              <!-- Quick Filters -->
              <div class="flex gap-1 border-left-1 border-gray-300 pl-2 ml-1">
                <Button
                  type="button"
                  icon="pi pi-server"
                  size="small"
                  :severity="activeTypeFilter === 'rest_api' ? 'info' : 'secondary'"
                  :outlined="activeTypeFilter !== 'rest_api'"
                  class="modern-button"
                  @click="toggleTypeFilter('rest_api')"
                  v-tooltip="'Show Web APIs'"/>
                <Button
                  type="button"
                  icon="pi pi-shield"
                  size="small"
                  :severity="activeSecurityFilter === 'secured' ? 'warning' : 'secondary'"
                  :outlined="activeSecurityFilter !== 'secured'"
                  class="modern-button"
                  @click="toggleSecurityFilter('secured')"
                  v-tooltip="'Show Protected Sources'"/>
              </div>
            </div>

            <div class="flex flex-column sm:flex-row gap-2 align-items-stretch sm:align-items-center">
              <!-- View Toggle - Hidden on mobile via CSS -->
              <Button
                type="button"
                :icon="isSimplifiedView ? 'pi pi-eye' : 'pi pi-eye-slash'"
                size="small"
                :severity="isSimplifiedView ? 'info' : 'secondary'"
                :outlined="!isSimplifiedView"
                class="modern-button hidden md:inline-flex"
                @click="toggleSimplifiedView"
                v-tooltip="isSimplifiedView ? 'Show all details' : 'Show essential info only'"/>
              
              <!-- Compact Pagination Control -->
              <div class="field m-0 flex align-items-center">
                <Dropdown
                  v-model="rowsPerPage"
                  :options="rowsPerPageOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="w-full sm:w-auto text-xs modern-dropdown"
                  size="small"
                  placeholder="Per page"/>
              </div>
              
              <!-- Compact Search Field -->
              <div class="search-container w-full sm:w-auto flex align-items-center">
                <IconField iconPosition="left" class="w-full modern-search">
                  <InputIcon>
                    <i class="pi pi-search"/>
                  </InputIcon>
                  <InputText
                    v-model="FeedFilters['global'].value"
                    placeholder="Search..."
                    class="border-round-lg w-full modern-input text-sm"/>
                </IconField>
              </div>
            </div>
          </div>

          <!-- Desktop View: DataTable -->
          <DataTable
              class="w-full p-datatable-lg hidden md:block"
              v-model:filters="FeedFilters"
              v-model:selection="selectedFeeds"
              :value="filteredSources"
              :sortOrder="-1"
              paginator
              :rows="rowsPerPage"
              :rowsPerPageOptions="rowsPerPageValues"
              dataKey="uid"
              id="feeds"
              @value-change="updateFilteredFeedsSize"
              role="table"
              aria-label="Data feeds table"
              aria-describedby="feeds-description">

            <!-- Checkbox Column -->
            <Column selectionMode="multiple" headerStyle="width: 3rem;" aria-label="Select feeds"></Column>

            <!-- Index Column with better styling -->
            <Column headerStyle="width: 4rem; text-align: center;" aria-label="Row number">
              <template #header>
                <div class="flex justify-content-center align-items-center w-full">
                  <i class="pi pi-hashtag text-slate-600" v-tooltip.top="'Row Number'"></i>
                </div>
              </template>
              <template #body="{ index }">
                <div class="bg-slate-100 text-slate-700 px-2 py-1 border-round text-center font-semibold text-sm border-l-3 border-slate-400">
                  {{ index + 1 }}
                </div>
              </template>
            </Column>

            <!-- Enhanced Name Column with modern typography -->
            <Column field="name" sortable headerStyle="padding: 1rem;" style="min-width: 15rem">
              <template #header>
                <div class="flex align-items-center gap-2">
                  <i class="pi pi-tag"></i>
                  <span>Name</span>
                </div>
              </template>
              <template #body="{ data }">
                <div class="flex align-items-center">
                  <div class="bg-blue-50 text-blue-700 p-2 border-round-xl mr-3 pi pi-database border-l-3 border-blue-400">
                  </div>
                  <div>
                    <div class="font-semibold text-slate-900 text-base">{{ data.name }}</div>
                  </div>
                </div>
              </template>
            </Column>

            <!-- Enhanced Provider Column -->
            <Column field="connection.provider" sortable headerStyle="padding: 1rem;" style="min-width: 12rem">
              <template #header>
                <div class="flex align-items-center gap-2">
                  <i class="pi pi-server"></i>
                  <span>Source Type</span>
                </div>
              </template>
              <template #body="{ data }">
                <div class="flex align-items-center gap-2">
                  <div 
                    class="provider-icon-wrapper flex align-items-center justify-content-center"
                    :class="getProviderIconClass(data.connection.provider)"
                    v-tooltip.top="getProviderTooltip(data.connection.provider)">
                    <i :class="getProviderIcon(data.connection.provider)" class="text-sm"></i>
                  </div>
                  <div class="flex flex-column">
                    <span class="provider-label font-semibold text-sm text-900">{{ getProviderLabel(data.connection.provider) }}</span>
                    <span class="provider-description text-xs text-600">{{ getProviderDescription(data.connection.provider) }}</span>
                  </div>
                </div>
              </template>
            </Column>

            <!-- Enhanced Auth Column -->
            <Column v-if="!isSimplifiedView" field="connection.auth.method" sortable headerStyle="padding: 1rem;" style="min-width: 11rem">
              <template #header>
                <div class="flex align-items-center gap-2">
                  <i class="pi pi-shield"></i>
                  <span>Security</span>
                </div>
              </template>
              <template #body="{ data }">
                <div class="flex align-items-center gap-2">
                  <div 
                    class="security-icon-wrapper flex align-items-center justify-content-center"
                    :class="getSecurityIconClass(data.connection.auth?.method)"
                    v-tooltip.top="getSecurityTooltip(data.connection.auth?.method)">
                    <i :class="getSecurityIcon(data.connection.auth?.method)" class="text-sm"></i>
                  </div>
                  <div class="flex flex-column">
                    <span class="security-label font-semibold text-sm text-900">{{ getSecurityLabel(data.connection.auth?.method) }}</span>
                    <span class="security-level text-xs" :class="getSecurityLevelClass(data.connection.auth?.method)">{{ getSecurityLevel(data.connection.auth?.method) }}</span>
                  </div>
                </div>
              </template>
            </Column>

            <!-- Enhanced URL Column with modern styling -->
            <Column v-if="!isSimplifiedView" field="connection.url" sortable headerStyle="padding: 1rem;" style="min-width: 20rem">
              <template #header>
                <div class="flex align-items-center gap-2">
                  <i class="pi pi-link"></i>
                  <span>URL</span>
                </div>
              </template>
              <template #body="{ data }">
                <div class="flex align-items-center gap-2 bg-slate-50 p-3 border-round-xl url-container border-l-3 border-slate-300">
                  <i class="pi pi-link text-blue-600"></i>
                  <code class="text-sm font-mono text-slate-700 flex-1 font-medium">
                    {{ safeTruncate(data.connection.url, 35) }}
                  </code>
                  <Button
                      size="small"
                      icon="pi pi-copy"
                      v-tooltip="'Copy URL'"
                      severity="info"
                      text
                      @click="copyUrl(data.connection.url)">
                  </Button>
                </div>
              </template>
            </Column>

            <!-- Enhanced Actions Column -->
            <Column field="actions" headerStyle="padding: 1rem; text-align: center;" :style="isSimplifiedView ? 'min-width: 12rem; width: 12rem' : 'max-width: 8rem; width: 8rem'" aria-label="Available actions">
              <template #header>
                <div class="flex align-items-center gap-2">
                  <i class="pi pi-cog"></i>
                  <span>Actions</span>
                </div>
              </template>
              <template #body="{ data }">
                <div class="flex gap-2">
                  <Button
                    v-tooltip.left="'View Feed'"
                    icon="pi pi-eye"
                    size="small"
                    severity="info"
                    outlined
                    @click="parseSource(data)"
                    :aria-label="`View feed data for ${data.name}`">
                  </Button>
                  <Button
                    v-tooltip.left="'Delete Feed'"
                    icon="pi pi-trash"
                    size="small"
                    severity="danger"
                    outlined
                    @click="confirmDelete(data)"
                    :aria-label="`Delete feed ${data.name}`">
                  </Button>
                </div>
              </template>
            </Column>

            <!-- Enhanced Footer -->
            <template #footer>
              <div class="p-2 text-center">
                <div class="bg-blue-100 text-blue-700 px-2 py-1 border-round text-sm font-medium inline-block">
                  {{ filteredFeeds }} of {{ sources.length }} feeds
                </div>
              </div>
            </template>
          </DataTable>

          <!-- Mobile View: Card-based layout -->
          <div class="feed-cards block md:hidden" role="list" aria-label="Feed cards list">
            <!-- Mobile Stats and Pagination Control -->
            <div class="bg-white border-1 border-gray-200 p-3 border-round mb-3 mobile-stats-minimal">
              <div class="flex justify-content-between align-items-center mb-2">
                <div class="flex align-items-center text-gray-700 text-sm">
                  <i class="pi pi-info-circle mr-2 text-blue-600"></i>
                  <span>Showing <strong>{{ Math.min(rowsPerPage === 0 ? filteredSources.length : rowsPerPage, filteredSources.length) }}</strong> of <strong>{{ filteredSources.length }}</strong> feeds</span>
                </div>
                <div class="flex gap-1">
                  <Tag v-if="selectedFeeds && selectedFeeds.length > 0" :value="`${selectedFeeds.length} selected`" severity="success" class="text-xs"/>
                </div>
              </div>
              <!-- Mobile Pagination Control -->
              <div class="flex justify-content-between align-items-center">
                <div class="text-xs text-gray-600">Items per page:</div>
                <Dropdown
                  v-model="rowsPerPage"
                  :options="rowsPerPageOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="w-auto text-xs"
                  size="small"
                  aria-label="Items per page"/>
              </div>
            </div>

            <!-- Card for each feed -->
            <div v-for="(feed, index) in paginatedMobileSources" :key="feed.uid" 
                 class="feed-card mb-3 border-round-lg shadow-2 overflow-hidden" 
                 role="listitem" 
                 :aria-label="`Feed: ${feed.name}`">
              <!-- Card Header with Name and Selection -->
              <div class="feed-card-header p-3 flex justify-content-between align-items-center">
                <div class="flex align-items-center">
                  <div class="bg-gray-100 text-gray-600 w-2rem h-2rem flex align-items-center justify-content-center border-circle text-center font-semibold text-sm mr-2">
                    {{ (currentMobilePage * rowsPerPage) + index + 1 }}
                  </div>
                  <Checkbox 
                    v-model="selectedFeeds" 
                    :value="feed" 
                    :inputId="'feed-' + feed.uid" 
                    binary 
                    class="mr-2"
                    :aria-label="`Select feed ${feed.name}`" />
                </div>
                <div class="flex align-items-center gap-1">
                  <div 
                    class="provider-icon-wrapper-mobile flex align-items-center justify-content-center"
                    :class="getProviderIconClass(feed.connection.provider)">
                    <i :class="getProviderIcon(feed.connection.provider)" class="text-xs"></i>
                  </div>
                  <span class="text-xs font-medium text-900">{{ getProviderLabel(feed.connection.provider) }}</span>
                </div>
              </div>

              <!-- Card Body with Feed Details -->
              <div class="feed-card-body p-3">
                <!-- Feed Name and Date -->
                <div class="flex align-items-center mb-2">
                  <div class="bg-blue-100 text-blue-600 p-2 border-circle mr-2 pi pi-database"></div>
                  <div class="flex-1 min-w-0">
                    <div class="font-semibold text-gray-800 truncate">{{ feed.name }}</div>
                  </div>
                </div>

                <!-- Security -->
                <div class="flex align-items-center mb-2">
                  <div class="text-gray-600 font-medium text-xs w-4rem">Protection:</div>
                  <div class="flex align-items-center gap-1">
                    <div 
                      class="security-icon-wrapper-mobile flex align-items-center justify-content-center"
                      :class="getSecurityIconClass(feed.connection.auth?.method)">
                      <i :class="getSecurityIcon(feed.connection.auth?.method)" class="text-xs"></i>
                    </div>
                    <span class="text-xs font-medium text-900">{{ getSecurityLabel(feed.connection.auth?.method) }}</span>
                  </div>
                </div>

                <!-- URL with Copy Button -->
                <div class="flex align-items-center gap-2 bg-gray-50 p-2 border-round mb-2">
                  <i class="pi pi-link text-blue-500 text-sm"></i>
                  <code class="text-xs font-mono text-gray-700 flex-1 truncate">
                    {{ feed.connection.url }}
                  </code>
                  <Button
                    size="small"
                    icon="pi pi-copy"
                    text
                    @click="copyUrl(feed.connection.url)"
                    :aria-label="`Copy URL ${feed.connection.url}`">
                  </Button>
                </div>
              </div>

              <!-- Card Footer with Actions -->
              <div class="feed-card-footer p-2 flex justify-content-end gap-2 bg-gray-50 border-top-1 border-gray-200">
                <Button
                  icon="pi pi-eye"
                  size="small"
                  severity="info"
                  outlined
                  class="p-button-sm"
                  v-tooltip.left="'View Feed Data'"
                  @click="parseSource(feed)"
                  :aria-label="`View feed data for ${feed.name}`">
                </Button>
                <Button
                  icon="pi pi-trash"
                  size="small"
                  severity="danger"
                  outlined
                  class="p-button-sm"
                  v-tooltip.left="'Delete Feed'"
                  @click="confirmDelete(feed)"
                  :aria-label="`Delete feed ${feed.name}`">
                </Button>
              </div>
            </div>
            
            <!-- Mobile Pagination Controls -->
            <div v-if="rowsPerPage > 0 && filteredSources.length > rowsPerPage" class="flex justify-content-center align-items-center gap-2 mt-4 p-3 bg-gray-50 border-round">
              <Button
                icon="pi pi-angle-left"
                size="small"
                :disabled="currentMobilePage === 0"
                @click="currentMobilePage--"
                class="p-button-outlined p-button-secondary"/>
              <span class="text-sm text-gray-600">
                Page {{ currentMobilePage + 1 }} of {{ Math.ceil(filteredSources.length / rowsPerPage) }}
              </span>
              <Button
                icon="pi pi-angle-right"
                size="small"
                :disabled="(currentMobilePage + 1) * rowsPerPage >= filteredSources.length"
                @click="currentMobilePage++"
                class="p-button-outlined p-button-secondary"/>
            </div>
          </div>
        </template>
      </Card>
      </main>
    </template>
  </BaseLayout>


<!-- Confirmation Dialog for Delete -->
<Dialog v-model:visible="deleteDialog" :style="{width: '450px'}" header="Delete Feed" :modal="true">
  <div class="flex flex-column align-items-center p-4">
    <div class="bg-red-100 text-red-600 p-3 border-circle mb-3 pi pi-exclamation-triangle">
    </div>
    <h3 class="text-xl font-semibold text-gray-800 mb-2">Delete Feed</h3>
    <p class="text-gray-600 mb-0 text-center" v-if="sourceToDelete">
      Are you sure you want to delete <strong>{{ sourceToDelete.name }}</strong>?
    </p>
    <div class="bg-red-50 border-left-3 border-red-500 p-3 mt-3 w-full">
      <div class="flex align-items-center">
        <i class="pi pi-info-circle text-red-600 mr-2"></i>
        <span class="text-red-700 text-sm">This action cannot be undone.</span>
      </div>
    </div>
  </div>
  <template #footer>
    <Button
      label="Cancel"
      icon="pi pi-times"
      severity="secondary"
      outlined
      class="mr-2"
      @click="deleteDialog = false" />
    <Button
      label="Delete"
      icon="pi pi-trash"
      severity="danger"
      @click="deleteSource"
      :loading="deleting" />
  </template>
</Dialog>

  <Toast>
    <template #undo="slotProps">
      <div class="flex align-items-center gap-3 w-full">
        <i :class="slotProps.message.icon" class="text-2xl text-green-600"></i>
        <div class="flex-1">
          <h4 class="m-0 font-semibold text-gray-800">{{ slotProps.message.summary }}</h4>
          <p class="m-0 text-sm text-gray-600 mt-1">{{ slotProps.message.detail }}</p>
        </div>
        <Button 
          label="Undo" 
          icon="pi pi-undo" 
          size="small"
          severity="info"
          outlined
          @click="undoDelete(slotProps.message.data.feedsToRestore, slotProps.message.data.isMultiple)"
          class="mr-2" />
        <Button 
          icon="pi pi-times" 
          text 
          size="small"
          @click="slotProps.onClose"
          class="text-gray-500" />
      </div>
    </template>
  </Toast>

  <!-- File input for import (hidden) -->
  <input
      ref="fileInput"
      type="file"
      accept=".json"
      style="display: none"
      @change="handleFileImport">

  <!-- Import Progress Dialog -->
<Dialog v-model:visible="importDialog" :style="{width: '450px'}" header="Import Feeds" :modal="true"
        :closable="!importing">
  <div v-if="!importing && !importResults">
    <div class="flex flex-column align-items-center p-4">
      <div class="bg-blue-100 text-blue-600 p-3 border-circle mb-3 pi pi-upload">
      </div>
      <h3 class="text-xl font-semibold text-gray-800 mb-2">Import Feeds</h3>
      <p class="text-gray-600 mb-3 text-center">Select a JSON file to import your feeds</p>
      <div class="bg-blue-50 border-left-3 border-blue-500 p-3 mb-3 w-full border-round">
        <div class="flex align-items-center">
          <i class="pi pi-info-circle text-blue-600 mr-2"></i>
          <span class="text-blue-700 text-sm">Supported format: JSON</span>
        </div>
      </div>
      <Button
        label="Choose File"
        icon="pi pi-file"
        severity="info"
        class="w-full"
        @click="triggerFileInput" />
    </div>
  </div>

  <div v-if="importing">
    <div class="flex flex-column align-items-center p-4">
      <div class="bg-blue-100 text-blue-500 p-4 border-circle inline-flex mb-3 pi pi-spinner pi-spin">
      </div>
      <h3 class="text-xl font-semibold text-gray-800 mb-2">Importing Feeds</h3>
      <p class="text-gray-600 mb-3">Please wait while we process your data</p>
      <div class="w-full bg-gray-200 rounded-full h-2.5 mb-2">
        <div class="bg-blue-600 h-2.5 rounded-full" :style="{width: `${(importProgress.current / importProgress.total) * 100}%`}"></div>
      </div>
      <div class="text-sm text-gray-500">
        {{ importProgress.current }} / {{ importProgress.total }} feeds processed
      </div>
    </div>
  </div>

  <div v-if="importResults">
    <div class="flex flex-column align-items-center p-4">
      <div class="bg-green-100 text-green-600 p-3 border-circle mb-3 pi pi-check-circle">
      </div>
      <h3 class="text-xl font-semibold text-gray-800 mb-3">Import Completed</h3>

      <div class="grid w-full">
        <div class="col-6">
          <div class="bg-gradient-to-br from-green-50 to-green-100 p-4 border-round-lg text-center shadow-1">
            <div class="text-green-600 text-3xl mb-2">
              <i class="pi pi-check"></i>
            </div>
            <div class="text-xl font-bold text-green-800">{{ importResults.success }}</div>
            <div class="text-sm text-green-600">Successful</div>
          </div>
        </div>
        <div class="col-6">
          <div class="bg-gradient-to-br from-red-50 to-red-100 p-4 border-round-lg text-center shadow-1">
            <div class="text-red-600 text-3xl mb-2">
              <i class="pi pi-times"></i>
            </div>
            <div class="text-xl font-bold text-red-800">{{ importResults.failed }}</div>
            <div class="text-sm text-red-600">Failed</div>
          </div>
        </div>
      </div>

      <div v-if="importResults.errors.length > 0" class="mt-4 w-full">
        <div class="font-semibold mb-2 text-gray-700">Error Details:</div>
        <div class="max-h-10rem overflow-auto bg-gray-50 p-3 border-round-lg">
          <div v-for="(error, index) in importResults.errors" :key="index" class="text-sm text-red-600 mb-2 flex align-items-start">
            <i class="pi pi-exclamation-circle mr-2 mt-1"></i>
            <span>{{ error }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <template #footer>
    <div v-if="!importing">
      <Button
        label="Cancel"
        icon="pi pi-times"
        severity="secondary"
        outlined
        class="mr-2"
        @click="closeImportDialog" />
      <Button
        v-if="importResults"
        label="Close"
        icon="pi pi-check"
        severity="success"
        @click="closeImportDialog" />
    </div>
  </template>
</Dialog>


  <!-- Batch Delete Confirmation Dialog -->
  <Dialog v-model:visible="batchDeleteDialog" :style="{width: '500px'}" header="Delete Multiple Feeds" :modal="true">
    <div class="flex flex-column">
      <div class="flex flex-column align-items-center p-4">
        <div class="bg-red-100 text-red-600 p-3 border-circle mb-3 pi pi-exclamation-triangle">
        </div>
        <h3 class="text-xl font-semibold text-gray-800 mb-2">Delete Multiple Feeds</h3>
        <p class="text-gray-600 mb-3 text-center">
          Are you sure you want to delete the selected feeds?
        </p>
      </div>

      <div class="mb-3">
        <div class="font-semibold mb-2">Feeds to be deleted ({{ selectedFeeds?.length || 0 }}):</div>
        <div class="max-h-15rem overflow-auto border-1 border-300 border-round-lg p-3 bg-gray-50">
          <div v-for="feed in selectedFeeds" :key="feed.uid" class="flex align-items-center mb-2 p-2 border-bottom-1 border-gray-200">
            <div class="bg-blue-100 text-blue-600 p-2 border-circle mr-2 pi pi-database">
            </div>
            <span class="font-medium">{{ feed.name }}</span>
            <Tag :value="feed.kind" severity="info" class="ml-2 text-xs"></Tag>
          </div>
        </div>
      </div>

      <div class="bg-red-50 border-left-3 border-red-500 p-3 border-round">
        <div class="flex align-items-center">
          <i class="pi pi-exclamation-circle text-red-600 mr-2"></i>
          <span class="text-red-700 font-semibold">Warning: This action cannot be undone!</span>
        </div>
      </div>
    </div>

  <template #footer>
    <Button
      label="Cancel"
      icon="pi pi-times"
      severity="secondary"
      outlined
      class="mr-2"
      @click="batchDeleteDialog = false" />
    <Button
      label="Delete All"
      icon="pi pi-trash"
      severity="danger"
      @click="batchDeleteFeeds"
      :loading="batchDeleting" />
  </template>

  </Dialog>

  <!-- Global Loading Component -->
  <GlobalLoading 
    :visible="globalLoading.visible" 
    :message="globalLoading.message"
    :progress="globalLoading.progress" />

</template>

<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from 'vue';
import {FilterMatchMode} from 'primevue/api';
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import InputIcon from "primevue/inputicon";
import IconField from "primevue/iconfield";
import Button from "primevue/button";
import Menu from "primevue/menu";
import Checkbox from "primevue/checkbox";
import Toast from "primevue/toast";
import Card from "primevue/card";
import Tag from "primevue/tag";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import ModernBreadcrumb from '@/components/ModernBreadcrumb.vue';
import StandardHeader from '@/components/StandardHeader.vue';
import BaseLayout from "@/components/BaseLayout.vue";
import {menuLinks} from '@/shared/sharedLinks.js';
import apiService from "@/services/apiService";
import copy from "copy-to-clipboard";
import {useToast} from "primevue/usetoast";
import {useRouter, useRoute} from 'vue-router';
import {defineStore} from "pinia";
import {useStore} from 'vuex';
import {useUndo} from '@/composables/useUndo';
import {useKeyboardShortcuts} from '@/composables/useKeyboardShortcuts';
import {useGlobalLoading} from '@/composables/useGlobalLoading';
import GlobalLoading from '@/components/GlobalLoading.vue';

const router = useRouter();
const route = useRoute();
const store = useStore();
const sideMenuLinks = ref(menuLinks);
const loadingFeeds = ref(false);
const toast = useToast();
const FeedFilters = ref();
const sources = ref([]);
const filteredFeeds = ref(0);

// Nouvelles fonctionnalités
const { undo, canUndo } = useUndo();
const { globalLoading, withLoading } = useGlobalLoading();

// Configuration des raccourcis clavier
useKeyboardShortcuts({
  'ctrl+z': () => {
    if (canUndo()) {
      undo();
    }
  },
  'ctrl+a': () => {
    if (sources.value.length > 0) {
      selectedFeeds.value = [...sources.value];
      toast.add({
        severity: 'info',
        summary: 'All selected',
        detail: `${sources.value.length} feeds selected`,
        life: 2000
      });
    }
  },
  'delete': () => {
    console.log('DELETE HANDLER CALLED - selectedFeeds:', selectedFeeds.value?.length, selectedFeeds.value);
    if (selectedFeeds.value?.length === 1) {
      // Single selection - use individual delete dialog
      console.log('Opening single delete dialog for:', selectedFeeds.value[0].name);
      confirmDelete(selectedFeeds.value[0]);
    } else if (selectedFeeds.value?.length > 1) {
      // Multiple selection - use batch delete
      console.log('Opening batch delete dialog for', selectedFeeds.value.length, 'feeds');
      confirmBatchDelete();
    } else {
      console.log('No feeds selected, showing warning toast');
      toast.add({
        severity: 'warn',
        summary: 'No selection',
        detail: 'Select feeds to delete first',
        life: 2000
      });
    }
  },
  'escape': () => {
    // Fermer les dialogues ouverts
    if (deleteDialog.value) {
      deleteDialog.value = false;
    }
    if (batchDeleteDialog.value) {
      batchDeleteDialog.value = false;
    }
    if (importDialog.value && !importing.value) {
      closeImportDialog();
    }
    // Désélectionner tout
    if (selectedFeeds.value?.length > 0) {
      selectedFeeds.value = [];
      toast.add({
        severity: 'info',
        summary: 'Selection cleared',
        detail: 'All feeds have been deselected',
        life: 2000
      });
    }
  },
  'ctrl+r': () => {
    withLoading(loadSources, 'Feeds reloading...');
  }
});

// Pagination settings
const rowsPerPage = ref(parseInt(localStorage.getItem('feedwiz_feeds_per_page') || '25'));
const currentMobilePage = ref(0);
const rowsPerPageOptions = ref([
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
]);
const rowsPerPageValues = ref([10, 25, 50, 100]);

// Computed property for paginated mobile sources
const paginatedMobileSources = computed(() => {
  if (rowsPerPage.value === 0) {
    return filteredSources.value;
  }
  const start = currentMobilePage.value * rowsPerPage.value;
  const end = start + rowsPerPage.value;
  return filteredSources.value.slice(start, end);
});
const filteredSources = computed(() => {
  let filtered = sources.value;

  // Apply text search filter
  if (FeedFilters.value && FeedFilters.value.global && FeedFilters.value.global.value) {
    const searchTerm = FeedFilters.value.global.value.toLowerCase();
    filtered = filtered.filter(source => {
      return source.name.toLowerCase().includes(searchTerm) ||
             source.kind.toLowerCase().includes(searchTerm) ||
             source.connection.url.toLowerCase().includes(searchTerm) ||
             (source.connection.provider && source.connection.provider.toLowerCase().includes(searchTerm));
    });
  }

  // Apply type filter
  if (activeTypeFilter.value) {
    if (activeTypeFilter.value === 'rest_api') {
      filtered = filtered.filter(source => source.kind === 'rest_api');
    } else if (activeTypeFilter.value === 'database') {
      filtered = filtered.filter(source => source.connection.provider === 'database');
    }
  }

  // Apply security filter
  if (activeSecurityFilter.value === 'secured') {
    filtered = filtered.filter(source => source.connection.auth && source.connection.auth.method);
  }

  return filtered;
});

// Delete functionality
const deleteDialog = ref(false);
const sourceToDelete = ref(null);
const deleting = ref(false);

// Batch delete functionality
const selectedFeeds = ref([]);
const batchDeleteDialog = ref(false);
const batchDeleting = ref(false);

// Quick filters functionality - initialized from URL params
const activeTypeFilter = ref(route.query.type || null);
const activeSecurityFilter = ref(route.query.security || null);

// Simplified view functionality - initialized from URL
const isSimplifiedView = ref(route.query.view !== 'detailed');


// Breadcrumb navigation
const breadcrumbItems = ref([
  {
    icon: 'pi pi-home',
    label: 'My Feeds',
    route: '/feeds' // Make it clickable like on Settings page
  }
]);


// Onboarding help dialog

const updateFilteredFeedsSize = (event) => {
  if (event) {
    filteredFeeds.value = event.length;
  }
};

const initFeedsFilters = () => {
  FeedFilters.value = {
    global: {value: route.query.search || null, matchMode: FilterMatchMode.CONTAINS},
    name: {value: null, matchMode: FilterMatchMode.STARTS_WITH},
    kind: {value: null, matchMode: FilterMatchMode.CONTAINS},
    'connection.provider': {value: null, matchMode: FilterMatchMode.CONTAINS},
    'connection.url': {value: null, matchMode: FilterMatchMode.CONTAINS},
  };
};

const clearFeedsFilter = () => {
  initFeedsFilters();
  activeTypeFilter.value = null;
  activeSecurityFilter.value = null;
  // Clear URL parameters
  updateURLFilters();
};

// Quick filter toggle functions
const toggleTypeFilter = (type) => {
  activeTypeFilter.value = activeTypeFilter.value === type ? null : type;
  updateURLFilters();
};

const toggleSecurityFilter = (filter) => {
  activeSecurityFilter.value = activeSecurityFilter.value === filter ? null : filter;
  updateURLFilters();
};

// URL state persistence
const updateURLFilters = () => {
  const query = { ...route.query };
  
  // Update search parameter
  if (FeedFilters.value?.global?.value) {
    query.search = FeedFilters.value.global.value;
  } else {
    delete query.search;
  }
  
  // Update type filter parameter
  if (activeTypeFilter.value) {
    query.type = activeTypeFilter.value;
  } else {
    delete query.type;
  }
  
  // Update security filter parameter
  if (activeSecurityFilter.value) {
    query.security = activeSecurityFilter.value;
  } else {
    delete query.security;
  }
  
  // Update view parameter
  if (!isSimplifiedView.value) {
    query.view = 'detailed';
  } else {
    delete query.view;
  }
  
  
  // Update URL without triggering navigation
  router.replace({ query });
};

// Simplified view toggle function
const toggleSimplifiedView = () => {
  isSimplifiedView.value = !isSimplifiedView.value;
  updateURLFilters();
};

// Watch for search input changes and update URL
watch(() => FeedFilters.value?.global?.value, () => {
  updateURLFilters();
}, { deep: true });

// Watch for route changes and update filters accordingly
watch(() => route.query, (newQuery) => {
  // Update filters from URL when user navigates back/forward
  if (FeedFilters.value) {
    FeedFilters.value.global.value = newQuery.search || null;
  }
  activeTypeFilter.value = newQuery.type || null;
  activeSecurityFilter.value = newQuery.security || null;
  isSimplifiedView.value = newQuery.view !== 'detailed';
}, { immediate: false });



initFeedsFilters();

const useFilterStore = defineStore('filters', {
  state: () => ({
    feedFilters: null
  }),
  actions: {
    setFeedFilters(filters) {
      this.feedFilters = filters;
    }
  }
});

const filterStore = useFilterStore();

const confirmDelete = (source) => {
  sourceToDelete.value = source;
  deleteDialog.value = true;
};

const deleteSource = async () => {
  if (!sourceToDelete.value) return;

  try {
    deleting.value = true;
    const feedToDelete = { ...sourceToDelete.value };
    
    // Call API to delete from backend first
    await apiService.deleteSource(feedToDelete.uid);
    
    // Only remove from UI after successful deletion from backend
    sources.value = sources.value.filter(s => s.uid !== feedToDelete.uid);
    filteredFeeds.value = filteredSources.value.length;
    
    // Close dialog
    deleteDialog.value = false;
    sourceToDelete.value = null;
    
    // Show success message
    toast.add({
      severity: 'success',
      summary: 'Success',
      description: `Feed "${feedToDelete.name}" deleted`,
      life: 3000
    });

  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `Failed to delete data source: ${error.message}`,
      life: 5000
    });
    deleteDialog.value = false;

  } finally {
    deleting.value = false;
  }
};

// In FeedsPage.vue, replace the dummy function
const parseSource = (data) => {
  // Navigate to the feed detail page with a route parameter to indicate Discovery tab
  router.push({
    path: `/feeds/${data.uid}`,
    query: {tab: 'discovery'}
  });
};


const copyUrl = (url) => {
  copy(url);
};

const safeTruncate = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text;
  const truncated = text.slice(0, maxLength);
  return truncated.slice(0, truncated.lastIndexOf(" ")) + "...";
}

const loadSources = async () => {
  try {
    loadingFeeds.value = true;
    const response = await apiService.getSources();
    sources.value = response.data_sources;
    return sources.value;
  } catch (error) {
    console.error("Error loading data sources:", error.message);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load data sources',
      life: 5000
    });
    return [];
  } finally {
    loadingFeeds.value = false;
  }
};

const navigateToSetup = () => {
  router.push('/feed/setup');
};

// Smart contextual menu items that adapt based on user state
const moreMenuItems = computed(() => {
  const baseItems = [];
  
  // Always available actions
  baseItems.push({
    label: 'Import Feeds',
    icon: 'pi pi-download',
    command: () => triggerFileInput()
  });
  
  // Export only if there are feeds to export
  if (sources.value.length > 0) {
    baseItems.push({
      label: selectedFeeds.value?.length > 0 
        ? `Export ${selectedFeeds.value.length} Selected` 
        : 'Export All Feeds',
      icon: 'pi pi-upload',
      command: () => selectedFeeds.value?.length > 0 ? exportSelectedFeeds() : exportFeeds()
    });
  }
  
  // Batch actions when feeds are selected
  if (selectedFeeds.value?.length > 0) {
    baseItems.push({ separator: true });
    
    baseItems.push({
      label: `Test ${selectedFeeds.value.length} Selected`,
      icon: 'pi pi-check-circle',
      command: () => testSelectedFeeds()
    });
    
    baseItems.push({
      label: `Parse ${selectedFeeds.value.length} Selected`,
      icon: 'pi pi-compass',
      command: () => parseSelectedFeeds()
    });
    
    if (selectedFeeds.value.length > 1) {
      baseItems.push({
        label: `Duplicate ${selectedFeeds.value.length} Feeds`,
        icon: 'pi pi-copy',
        command: () => duplicateSelectedFeeds()
      });
    }
  }
  
  // Context-specific actions based on current filter/tab
  if (sources.value.length > 0) {
    baseItems.push({ separator: true });
    
    
    // Quick action suggestions based on feed health
    const failedFeeds = sources.value.filter(feed => feed.status === 'failed').length;
    if (failedFeeds > 0) {
      baseItems.push({
        label: `Fix ${failedFeeds} Failed Feeds`,
        icon: 'pi pi-exclamation-triangle',
        severity: 'warn',
        command: () => showFailedFeedsHelp()
      });
    }
  }
  
  return baseItems;
});

// More menu ref and toggle function
const moreMenu = ref();

const toggleMoreMenu = (event) => {
  moreMenu.value.toggle(event);
};

// Header actions for StandardHeader component
const headerActions = computed(() => [
  {
    icon: 'pi pi-cog',
    label: 'More',
    mobileLabel: '',
    size: 'small',
    mobileSize: 'large',
    severity: 'secondary',
    outlined: true,
    tooltip: 'More options',
    command: toggleMoreMenu
  },
  {
    icon: 'pi pi-plus',
    label: 'New Feed',
    mobileLabel: 'New Feed',
    size: 'small',
    mobileSize: 'large',
    severity: 'success',
    command: navigateToSetup
  }
]);


// Gestion du focus pour les raccourcis clavier après sélection
const handleDocumentClick = (event) => {
  // Vérifier si c'est un clic sur une checkbox de sélection
  if (event.target.matches('input[type="checkbox"]') || 
      event.target.closest('.p-checkbox')) {
    // Petit délai pour permettre à PrimeVue de gérer la sélection
    setTimeout(() => {
      // Rendre le focus au document pour permettre les raccourcis clavier
      if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }
      document.body.focus();
    }, 50);
  }
};

onMounted(() => {
  if (filterStore.feedFilters) {
    FeedFilters.value = filterStore.feedFilters;
  } else {
    initFeedsFilters();
  }
  loadSources().then(() => {
    // Initialize filteredFeeds count after sources are loaded
    filteredFeeds.value = sources.value.length;
  });

  // Check if user just registered and show success message
  if (store.getters.registerSuccess) {
    toast.add({
      severity: 'success',
      summary: 'Registration Successful',
      detail: 'Your account has been created successfully. Welcome to FeedWiz!',
      life: 5000
    });

    // Reset the register success state
    store.commit('setRegisterSuccess', false);
  }


  document.addEventListener('click', handleDocumentClick);
});

// Remove event listener on unmount
onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick);
});

watch(FeedFilters, () => {
  filterStore.setFeedFilters(FeedFilters.value);
}, {deep: true});

// Watch filteredSources to update the count
watch(filteredSources, (newValue) => {
  filteredFeeds.value = newValue.length;
  // Reset mobile page when filters change
  currentMobilePage.value = 0;
});

// Watch rowsPerPage to persist user preference and reset mobile page
watch(rowsPerPage, (newValue) => {
  localStorage.setItem('feedwiz_feeds_per_page', newValue.toString());
  currentMobilePage.value = 0;
});

// Import functionality
const fileInput = ref(null);
const importDialog = ref(false);
const importing = ref(false);
const importResults = ref(null);
const importProgress = ref({current: 0, total: 0});

const triggerFileInput = () => {
  fileInput.value.click();
};

const handleFileImport = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Reset file input
  event.target.value = '';

  try {
    const text = await file.text();
    const feedsData = JSON.parse(text);

    if (!Array.isArray(feedsData)) {
      toast.add({
        severity: 'error',
        summary: 'Invalid Format',
        detail: 'The imported file must contain an array of feeds',
        life: 5000
      });
      return;
    }

    await importFeeds(feedsData);

  } catch (error) {
    console.error('Error reading file:', error);
    toast.add({
      severity: 'error',
      summary: 'File Error',
      detail: 'Failed to read the selected file. Please ensure it\'s a valid JSON file.',
      life: 5000
    });
  }
};

const importFeeds = async (feedsData) => {
  importing.value = true;
  importDialog.value = true;
  importResults.value = null;
  importProgress.value = {current: 0, total: feedsData.length};

  const results = {
    success: 0,
    failed: 0,
    errors: []
  };

  for (let i = 0; i < feedsData.length; i++) {
    const feedData = feedsData[i];
    importProgress.value.current = i + 1;

    try {
      // Validate required fields
      if (!feedData.name || !feedData.connection?.url) {
        throw new Error(`Feed ${i + 1}: Missing required fields (name or connection.url)`);
      }

      // Transform the imported data to match the API format
      const sourceData = {
        name: feedData.name,
        description: feedData.description || null,
        kind: feedData.kind || 'rest_api',
        connection: {
          provider: feedData.connection.provider || 'generic',
          url: feedData.connection.url,
          auth: feedData.connection.auth || null,
          options: feedData.connection.options || {}
        }
      };

      // Create the feed via API
      await apiService.createSource(sourceData);
      results.success++;

    } catch (error) {
      console.error(`Error importing feed ${i + 1}:`, error);
      results.failed++;
      results.errors.push(`Feed "${feedData.name || 'Unknown'}": ${error.message}`);
    }

    // Small delay to prevent overwhelming the API
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  importing.value = false;
  importResults.value = results;

  // Reload the feeds list to show the imported feeds
  const updatedSources = await loadSources();
  filteredFeeds.value = updatedSources.length;

  // Show summary toast
  if (results.success > 0) {
    toast.add({
      severity: 'success',
      summary: 'Import Completed',
      detail: `Successfully imported ${results.success} feeds`,
      life: 3000
    });
  }

  if (results.failed > 0) {
    toast.add({
      severity: 'warn',
      summary: 'Import Issues',
      detail: `${results.failed} feeds failed to import`,
      life: 5000
    });
  }
};

const closeImportDialog = () => {
  importDialog.value = false;
  importing.value = false;
  importResults.value = null;
  importProgress.value = {current: 0, total: 0};
};

const exportFeeds = () => {
  try {
    // Préparer les données pour l'export
    const exportData = sources.value.map(source => ({
      name: source.name,
      description: source.description,
      kind: source.kind,
      connection: {
        provider: source.connection.provider,
        url: source.connection.url,
        auth: source.connection.auth || null,
        options: source.connection.options || {}
      },
      created_at: source.created_at,
      updated_at: source.updated_at
    }));

    // Créer le contenu JSON
    const jsonContent = JSON.stringify(exportData, null, 2);

    // Créer un blob et télécharger en utilisant l'API native
    const blob = new Blob([jsonContent], {type: 'application/json'});
    const url = URL.createObjectURL(blob);

    // Créer un lien temporaire pour le téléchargement
    const link = document.createElement('a');
    link.href = url;
    link.download = `feeds_export_${new Date().toISOString().split('T')[0]}.json`;

    // Déclencher le téléchargement
    document.body.appendChild(link);
    link.click();

    // Nettoyer
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting feeds:', error);
    toast.add({
      severity: 'error',
      summary: 'Export Failed',
      detail: 'Failed to export feeds',
      life: 5000
    });
  }
};

// Batch delete functions
const confirmBatchDelete = () => {
  if (!selectedFeeds.value || selectedFeeds.value.length === 0) return;
  batchDeleteDialog.value = true;
};

const batchDeleteFeeds = async () => {
  if (!selectedFeeds.value || selectedFeeds.value.length === 0) return;

  try {
    batchDeleting.value = true;
    const feedsToDelete = [...selectedFeeds.value];
    
    // Call API to delete all feeds from backend first
    const deletePromises = feedsToDelete.map(feed => 
      apiService.deleteSource(feed.uid)
    );
    await Promise.all(deletePromises);
    
    // Only remove from UI after successful deletion from backend
    const deletedIds = feedsToDelete.map(feed => feed.uid);
    sources.value = sources.value.filter(source => !deletedIds.includes(source.uid));
    filteredFeeds.value = filteredSources.value.length;
    
    // Clear selection and close dialog
    selectedFeeds.value = [];
    batchDeleteDialog.value = false;
    
    // Show success message
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `${feedsToDelete.length} feeds deleted successfully`,
      life: 3000
    });

  } catch (error) {
    console.error('Error in batch delete:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `Batch delete failed: ${error.message}`,
      life: 5000
    });
    batchDeleteDialog.value = false;
  } finally {
    batchDeleting.value = false;
  }
};



// Computed properties for stats
const activeFeeds = computed(() => sources.value.length);
const authenticatedFeeds = computed(() =>
    sources.value.filter(feed => feed.connection.auth).length
);
const restApiFeeds = computed(() =>
    sources.value.filter(feed => feed.kind === 'rest_api').length
);

// Helper functions for enhanced display

const getProviderIcon = (provider) => {
  const iconMap = {
    'generic': 'pi pi-globe',
    'rest_api': 'pi pi-server',
    'json_api': 'pi pi-code',
    'xml_api': 'pi pi-file-edit',
    'csv_api': 'pi pi-table',
    'database': 'pi pi-database',
    'file': 'pi pi-file'
  };
  return iconMap[provider] || 'pi pi-globe';
};

// Enhanced Provider helper functions
const getProviderLabel = (provider) => {
  const labelMap = {
    'generic': 'Web Source',
    'rest_api': 'Web API',
    'json_api': 'JSON Data',
    'xml_api': 'XML Data',
    'csv_api': 'Spreadsheet Data',
    'database': 'Database',
    'file': 'Files'
  };
  return labelMap[provider] || 'Web Source';
};

const getProviderDescription = (provider) => {
  const descMap = {
    'generic': 'Web connection',
    'rest_api': 'Online service',
    'json_api': 'JSON data feed',
    'xml_api': 'XML data feed',
    'csv_api': 'Spreadsheet format',
    'database': 'Direct database',
    'file': 'File access'
  };
  return descMap[provider] || 'Web connection';
};

const getProviderIconClass = (provider) => {
  const classMap = {
    'generic': 'w-2rem h-2rem border-round-md bg-gray-50 text-gray-600 border-l-3 border-gray-400',
    'rest_api': 'w-2rem h-2rem border-round-md bg-blue-50 text-blue-600 border-l-3 border-blue-400',
    'json_api': 'w-2rem h-2rem border-round-md bg-green-50 text-green-600 border-l-3 border-green-400',
    'xml_api': 'w-2rem h-2rem border-round-md bg-orange-50 text-orange-600 border-l-3 border-orange-400',
    'csv_api': 'w-2rem h-2rem border-round-md bg-purple-50 text-purple-600 border-l-3 border-purple-400',
    'database': 'w-2rem h-2rem border-round-md bg-teal-50 text-teal-600 border-l-3 border-teal-400',
    'file': 'w-2rem h-2rem border-round-md bg-yellow-50 text-yellow-600 border-l-3 border-yellow-400'
  };
  return classMap[provider] || 'w-2rem h-2rem border-round-md bg-gray-50 text-gray-600 border-l-3 border-gray-400';
};

const getProviderTooltip = (provider) => {
  const tooltipMap = {
    'generic': 'Standard web connection - Works with most online data sources',
    'rest_api': 'Web API - Connects to online services and applications',
    'json_api': 'JSON Data - Structured data in JSON format',
    'xml_api': 'XML Data - Structured data in XML format',
    'csv_api': 'Spreadsheet Data - Table data like Excel or CSV files',
    'database': 'Database - Direct connection to databases',
    'file': 'File Access - Connect to files and folders'
  };
  return tooltipMap[provider] || 'Standard web connection';
};

// Security helper functions
const getSecurityIcon = (method) => {
  const iconMap = {
    'bearer': 'pi pi-key',
    'basic': 'pi pi-lock',
    'oauth': 'pi pi-shield',
    'api_key': 'pi pi-id-card',
    'jwt': 'pi pi-verified',
    'oauth2': 'pi pi-shield',
    'none': 'pi pi-unlock',
    null: 'pi pi-unlock',
    undefined: 'pi pi-unlock'
  };
  return iconMap[method] || 'pi pi-unlock';
};

const getSecurityLabel = (method) => {
  const labelMap = {
    'bearer': 'Secure Token',
    'basic': 'Username & Password',
    'oauth': 'OAuth Login',
    'api_key': 'Access Key',
    'jwt': 'JWT Token',
    'oauth2': 'OAuth 2.0',
    'none': 'Open Access',
    null: 'Open Access',
    undefined: 'Open Access'
  };
  return labelMap[method] || 'Open Access';
};

const getSecurityLevel = (method) => {
  const levelMap = {
    'bearer': 'Highly Protected',
    'basic': 'Password Protected',
    'oauth': 'Highly Protected',
    'api_key': 'Key Protected',
    'jwt': 'Highly Protected',
    'oauth2': 'Highly Protected',
    'none': 'Public Access',
    null: 'Public Access',
    undefined: 'Public Access'
  };
  return levelMap[method] || 'Public Access';
};

const getSecurityLevelClass = (method) => {
  const classMap = {
    'bearer': 'text-green-600 font-medium',
    'basic': 'text-yellow-600 font-medium',
    'oauth': 'text-green-600 font-medium',
    'api_key': 'text-blue-600 font-medium',
    'jwt': 'text-green-600 font-medium',
    'oauth2': 'text-green-600 font-medium',
    'none': 'text-red-600 font-medium',
    null: 'text-red-600 font-medium',
    undefined: 'text-red-600 font-medium'
  };
  return classMap[method] || 'text-red-600 font-medium';
};

const getSecurityIconClass = (method) => {
  const classMap = {
    'bearer': 'w-2rem h-2rem border-round-md bg-green-50 text-green-600 border-l-3 border-green-400',
    'basic': 'w-2rem h-2rem border-round-md bg-yellow-50 text-yellow-600 border-l-3 border-yellow-400',
    'oauth': 'w-2rem h-2rem border-round-md bg-emerald-50 text-emerald-600 border-l-3 border-emerald-400',
    'api_key': 'w-2rem h-2rem border-round-md bg-blue-50 text-blue-600 border-l-3 border-blue-400',
    'jwt': 'w-2rem h-2rem border-round-md bg-indigo-50 text-indigo-600 border-l-3 border-indigo-400',
    'oauth2': 'w-2rem h-2rem border-round-md bg-teal-50 text-teal-600 border-l-3 border-teal-400',
    'none': 'w-2rem h-2rem border-round-md bg-red-50 text-red-600 border-l-3 border-red-400',
    null: 'w-2rem h-2rem border-round-md bg-red-50 text-red-600 border-l-3 border-red-400',
    undefined: 'w-2rem h-2rem border-round-md bg-red-50 text-red-600 border-l-3 border-red-400'
  };
  return classMap[method] || 'w-2rem h-2rem border-round-md bg-red-50 text-red-600 border-l-3 border-red-400';
};

const getSecurityTooltip = (method) => {
  const tooltipMap = {
    'bearer': 'Secure Token - Uses encrypted tokens for maximum security',
    'basic': 'Username & Password - Standard login with credentials',
    'oauth': 'OAuth - Secure login through trusted providers',
    'api_key': 'Access Key - Uses special keys for authentication',
    'jwt': 'JWT Token - Advanced token-based security',
    'oauth2': 'OAuth 2.0 - Modern secure login standard',
    'none': 'Open Access - No password required, publicly available',
    null: 'Open Access - No password required, publicly available',
    undefined: 'Open Access - No password required, publicly available'
  };
  return tooltipMap[method] || 'Open access - no password required';
};

// Smart contextual action functions
const exportSelectedFeeds = () => {
  if (!selectedFeeds.value?.length) return;
  
  try {
    const exportData = selectedFeeds.value.map(source => ({
      name: source.name,
      description: source.description,
      kind: source.kind,
      connection: {
        provider: source.connection.provider,
        url: source.connection.url,
        auth: source.connection.auth || null,
        options: source.connection.options || {}
      },
      created_at: source.created_at,
      updated_at: source.updated_at
    }));

    const jsonContent = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonContent], {type: 'application/json'});
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `selected_feeds_export_${new Date().toISOString().split('T')[0]}.json`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.add({
      severity: 'success',
      summary: 'Export Successful',
      detail: `Exported ${selectedFeeds.value.length} selected feeds`,
      life: 3000
    });
  } catch (error) {
    console.error('Error exporting selected feeds:', error);
    toast.add({
      severity: 'error',
      summary: 'Export Failed',
      detail: 'Failed to export selected feeds',
      life: 5000
    });
  }
};

const testSelectedFeeds = async () => {
  if (!selectedFeeds.value?.length) return;
  
  toast.add({
    severity: 'info',
    summary: 'Testing feeds',
    detail: `Testing ${selectedFeeds.value.length} selected feeds...`,
    life: 3000
  });
  
  // In a real implementation, this would test each connection
  // For now, we'll simulate the process
  setTimeout(() => {
    const successful = Math.floor(selectedFeeds.value.length * 0.8);
    const failed = selectedFeeds.value.length - successful;
    
    toast.add({
      severity: successful === selectedFeeds.value.length ? 'success' : 'warn',
      summary: 'Feed Test Complete',
      detail: `${successful} working, ${failed} failed`,
      life: 5000
    });
  }, 2000);
};

const parseSelectedFeeds = () => {
  if (!selectedFeeds.value?.length) return;
  
  // Navigate to a batch parsing view or handle multiple feeds
  toast.add({
    severity: 'info',
    summary: 'Batch Parsing',
    detail: `Starting batch parsing of ${selectedFeeds.value.length} feeds`,
    life: 3000
  });
  
  // Open each feed in new tabs for parsing
  selectedFeeds.value.forEach(feed => {
    const url = router.resolve({
      path: `/feeds/${feed.uid}`,
      query: { tab: 'discovery' }
    });
    window.open(url.href, '_blank');
  });
};

const duplicateSelectedFeeds = async () => {
  if (!selectedFeeds.value?.length) return;
  
  try {
    toast.add({
      severity: 'info',
      summary: 'Duplicating feeds',
      detail: `Creating copies of ${selectedFeeds.value.length} feeds...`,
      life: 3000
    });
    
    const duplicatePromises = selectedFeeds.value.map(async (feed, index) => {
      const duplicateData = {
        name: `${feed.name} (Copy ${index + 1})`,
        kind: feed.kind,
        connection: {
          provider: feed.connection.provider,
          url: feed.connection.url,
          auth: feed.connection.auth ? { ...feed.connection.auth } : null,
          options: feed.connection.options ? { ...feed.connection.options } : {}
        }
      };
      
      return apiService.createSource(duplicateData);
    });
    
    const results = await Promise.allSettled(duplicatePromises);
    const successful = results.filter(result => result.status === 'fulfilled').length;
    const failed = results.filter(result => result.status === 'rejected').length;
    
    // Reload sources to show the new duplicates
    await loadSources();
    
    toast.add({
      severity: successful > 0 ? 'success' : 'error',
      summary: 'Duplication Complete',
      detail: `${successful} feeds duplicated${failed > 0 ? `, ${failed} failed` : ''}`,
      life: 5000
    });
    
  } catch (error) {
    console.error('Error duplicating feeds:', error);
    toast.add({
      severity: 'error',
      summary: 'Duplication Failed',
      detail: 'Failed to duplicate selected feeds',
      life: 5000
    });
  }
};



const showFailedFeedsHelp = () => {
  const failedFeeds = sources.value.filter(feed => feed.status === 'failed');
  
  toast.add({
    severity: 'info',
    summary: 'Feed Help',
    detail: `Opening diagnostic help for ${failedFeeds.length} failed feeds`,
    life: 3000
  });
  
  // In a real implementation, this could open a help dialog or diagnostic tool
  // For now, we'll filter to show only failed feeds
  activeTypeFilter.value = null;
  activeSecurityFilter.value = null;
  
  // Set a custom filter for failed feeds
  if (FeedFilters.value) {
    FeedFilters.value.global.value = 'failed';
  }
  
  updateURLFilters();
};

// Undo system for better UX
const undoTimeouts = ref(new Map());

const undoDelete = (feedsToRestore, isMultiple = false) => {
  // Cancel the actual deletion timeout
  const key = isMultiple ? 'batch' : feedsToRestore[0]?.uid;
  const timeoutId = undoTimeouts.value.get(key);
  if (timeoutId) {
    clearTimeout(timeoutId);
    undoTimeouts.value.delete(key);
  }
  
  // Restore feeds to the UI
  feedsToRestore.forEach(feed => {
    const index = sources.value.findIndex(s => s.uid === feed.uid);
    if (index === -1) {
      sources.value.push(feed);
    }
  });
  
  toast.add({
    severity: 'info',
    summary: 'Deletion Undone',
    detail: `${isMultiple ? 'Feeds' : 'Feed'} restored successfully`,
    life: 3000
  });
};


</script>

<style scoped>
/* Modern Hero Section - matching FeedDetail style */
.hero-section {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  color: #1f2937;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.hero-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
}

.hero-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.hero-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.hero-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  letter-spacing: -0.025em;
}

.hero-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  font-weight: 400;
}

.hero-actions {
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
}

/* Global button enhancements */
:deep(.p-button) {
  transition: all 0.2s ease;
}

:deep(.p-button:hover) {
  transform: translateY(-1px);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .hero-content-mobile {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
    padding: 1rem;
  }

  .hero-header-mobile {
    justify-content: center;
    width: 100%;
  }

  .hero-text-mobile {
    text-align: center;
  }

  .hero-title-mobile {
    font-size: 1.25rem;
    margin-bottom: 0.5rem;
  }

  .hero-actions-mobile {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 0.5rem;
    width: 100%;
    margin-top: 1rem;
  }

  .hero-button-mobile {
    width: 100%;
    justify-content: center;
  }
}

/* Updated header for stats section */
.stats-header {
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb !important;
  width: 100% !important;
  margin: 0 !important;
  border-radius: 0 !important;
}

/* CSS personnalisé pour les gradients Tailwind */
/* CSS Grid and responsive optimizations */

.bg-gradient-to-br {
  background: linear-gradient(to bottom right, var(--from-color), var(--to-color));
}

.from-blue-600 {
  --from-color: #2563eb;
}

.to-purple-600 {
  --to-color: #9333ea;
}

.from-gray-50 {
  --from-color: #f9fafb;
}

.to-blue-50 {
  --to-color: #eff6ff;
}

.to-teal-50 {
  --to-color: #f0fdfa;
}

.from-blue-50 {
  --from-color: #eff6ff;
}

.to-indigo-50 {
  --to-color: #eef2ff;
}

.from-green-50 {
  --from-color: #ecfdf5;
}

.to-blue-50 {
  --to-color: #eff6ff;
}

.from-gray-400 {
  --from-color: #9ca3af;
}

.from-gray-500 {
  --from-color: #6b7280;
}

.to-gray-500 {
  --to-color: #6b7280;
}

.to-gray-600 {
  --to-color: #4b5563;
}

.from-blue-400 {
  --from-color: #60a5fa;
}

.from-blue-500 {
  --from-color: #3b82f6;
}

.to-blue-500 {
  --to-color: #3b82f6;
}

.to-blue-600 {
  --to-color: #2563eb;
}

.from-red-500 {
  --from-color: #ef4444;
}

.to-red-600 {
  --to-color: #dc2626;
}

.from-green-500 {
  --from-color: #10b981;
}

.to-green-600 {
  --to-color: #059669;
}

.to-teal-500 {
  --to-color: #14b8a6;
}

/* Enhanced modern styling classes */
.bg-opacity-20 {
  background-color: rgba(255, 255, 255, 0.2);
}

.bg-opacity-10 {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Screen reader only text */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Skip link for keyboard navigation */
.skip-link {
  position: absolute;
  top: -40px;
  left: 6px;
  background: white;
  color: #1d4ed8;
  padding: 8px 16px;
  border-radius: 6px;
  border: 2px solid #1d4ed8;
  font-weight: 600;
  text-decoration: none;
  z-index: 9999;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.skip-link:focus {
  top: 8px;
  outline: none;
}

/* Enhanced focus indicators for accessibility */
:deep(.p-button:focus),
:deep(.p-inputtext:focus),
:deep(.p-dropdown:focus),
:deep(.p-checkbox:focus),
:deep(.p-checkbox > .p-checkbox-box:focus),
:deep(.p-datatable .p-checkbox .p-checkbox-box:focus),
button:focus,
input:focus,
select:focus,
textarea:focus,
[tabindex]:focus {
  outline: 3px solid #2563eb !important;
  outline-offset: 2px !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.3) !important;
  border-color: #2563eb !important;
  z-index: 10;
  position: relative;
}

/* Skip link focus styling */
a[href="#main-content"]:focus {
  outline: 3px solid #2563eb !important;
  outline-offset: 2px !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.3) !important;
  text-decoration: none;
  transform: scale(1.05);
}

/* DataTable row focus indicators */
:deep(.p-datatable .p-datatable-tbody > tr:focus),
:deep(.p-datatable .p-datatable-tbody > tr:focus-within) {
  outline: 2px solid #2563eb !important;
  outline-offset: -2px !important;
  background-color: rgba(37, 99, 235, 0.1) !important;
  position: relative;
  z-index: 1;
}

/* Card focus indicators for mobile */
.feed-card:focus,
.feed-card:focus-within {
  outline: 2px solid #2563eb !important;
  outline-offset: 2px !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.3) !important;
  transform: translateY(-1px);
}

/* Stats cards focus */
.stats-card:focus,
.stats-card:focus-within {
  outline: 2px solid #2563eb !important;
  outline-offset: 2px !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.3) !important;
}

/* Dialog focus management */
:deep(.p-dialog .p-dialog-content:focus),
:deep(.p-dialog .p-dialog-header:focus) {
  outline: 2px solid #2563eb !important;
  outline-offset: -2px !important;
}

/* Menu focus */
:deep(.p-menu .p-menuitem-link:focus) {
  outline: 2px solid #2563eb !important;
  outline-offset: -2px !important;
  background-color: rgba(37, 99, 235, 0.1) !important;
}

/* Breadcrumb focus */
:deep(.p-breadcrumb .p-menuitem-link:focus) {
  outline: 2px solid #2563eb !important;
  outline-offset: 2px !important;
  background-color: rgba(37, 99, 235, 0.1) !important;
  border-radius: 0.25rem;
}

/* Warm color gradients */
.from-orange-50 {
  --from-color: #fff7ed;
}

.to-pink-50 {
  --to-color: #fdf2f8;
}

.from-orange-400 {
  --from-color: #fb923c;
}

.to-pink-500 {
  --to-color: #ec4899;
}

.from-amber-50 {
  --from-color: #fffbeb;
}

.from-emerald-50 {
  --from-color: #ecfdf5;
}

.from-violet-50 {
  --from-color: #f5f3ff;
}

.to-indigo-100 {
  --to-color: #e0e7ff;
}

.backdrop-blur {
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.border-left-3 {
  border-left: 3px solid;
}

.transition-all {
  transition: all 0.3s ease;
}

.transition-duration-300 {
  transition-duration: 300ms;
}

/* Modern table header styling */
:deep(.p-datatable .p-datatable-thead > tr > th) {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #bae6fd 100%) !important;
  border-bottom: 2px solid #0ea5e9 !important;
  font-weight: 600 !important;
  color: #0c4a6e !important;
  padding: 1rem !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

:deep(.p-datatable .p-datatable-thead > tr > th:first-child) {
  border-top-left-radius: 0.75rem;
}

:deep(.p-datatable .p-datatable-thead > tr > th:last-child) {
  border-top-right-radius: 0.75rem;
}

:deep(.p-datatable .p-datatable-thead > tr > th .p-column-header-content) {
  justify-content: flex-start;
}

:deep(.p-datatable .p-datatable-tbody > tr:hover) {
  background-color: #f8fafc !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 0.75rem !important;
}

/* Modern typography classes */
.title-modern {
  letter-spacing: -0.025em;
  line-height: 1.2;
}

.subtitle-modern {
  letter-spacing: -0.01em;
  line-height: 1.4;
}

.label-modern {
  letter-spacing: -0.01em;
}

/* Enhanced component styles */
.main-content-card {
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.header-section {
  backdrop-filter: blur(5px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.controls-section {
  backdrop-filter: blur(5px);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.modern-button {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
}

.cta-button {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.cta-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.cta-button:hover::before {
  left: 100%;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(34, 197, 94, 0.2);
}

.modern-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.modern-button {
  border-color: #e5e7eb !important;
}

.modern-button:hover {
  border-color: #d1d5db !important;
  background: #f9fafb !important;
}

.modern-dropdown {
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.modern-search {
  transition: all 0.2s ease;
}

.modern-input {
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.modern-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  transform: translateY(-1px);
}

.modern-icon-button {
  transition: all 0.2s ease;
}

.modern-icon-button:hover {
  transform: scale(1.1);
  color: #2563eb;
}

.url-container {
  backdrop-filter: blur(5px);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.url-container:hover {
  border-color: #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.hover\:shadow-3:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.hover\:shadow-4:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.shadow-1 {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.shadow-2 {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.shadow-soft {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04);
}

.shadow-modern {
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(0, 0, 0, 0.06);
}

.min-w-6rem {
  min-width: 6rem;
}

.min-w-20rem {
  min-width: 20rem;
}

/* Enhanced CSS Grid for stats */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.5rem;
}

/* Responsive styles for mobile */
.stats-card {
  display: flex;
  min-height: 44px;
  min-width: 160px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  /* Touch-friendly padding */
  padding: 1rem !important;
}

.stats-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.3));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.stats-card:hover::before {
  opacity: 1;
}

/* Minimal styling classes */
.header-minimal {
  background: #ffffff !important;
  border-bottom: 1px solid #e5e7eb;
}

.icon-minimal {
  background: #2563eb !important;
  transition: all 0.2s ease;
}

.icon-minimal:hover {
  background: #1d4ed8 !important;
  transform: translateY(-1px);
}

.controls-minimal {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}

.footer-minimal {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb;
}

.mobile-stats-minimal {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}

/* Enhanced table header with minimal colors */
:deep(.p-datatable .p-datatable-thead > tr > th) {
  background: #f9fafb !important;
  border-bottom: 2px solid #e5e7eb !important;
  color: #374151 !important;
}

.hover-lift:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.hover-lift-warm:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.15s ease;
}

/* Sparkle animation */
@keyframes sparkle {
  0%, 100% { transform: scale(1) rotate(0deg); }
  25% { transform: scale(1.1) rotate(5deg); }
  50% { transform: scale(1.05) rotate(-3deg); }
  75% { transform: scale(1.08) rotate(2deg); }
}

@keyframes pulse-gentle {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.02); opacity: 0.9; }
}

.sparkle-icon {
  animation: sparkle 3s ease-in-out infinite;
  filter: drop-shadow(0 0 10px rgba(251, 146, 60, 0.3));
}

.animate-pulse-gentle {
  animation: pulse-gentle 2s ease-in-out infinite;
}

.sparkle-container {
  position: relative;
  overflow: visible;
}


/* ===== MEDIUM TABLET (768px+) ===== */
@media (min-width: 768px) {
  /* Tablet layout optimizations */
  .stats-card {
    flex: 1 0 calc(50% - 0.5rem);
    min-width: 200px;
    padding: 1rem !important;
  }

  /* Optimize DataTable for tablets */
  :deep(.p-datatable .p-datatable-thead > tr > th) {
    padding: 0.75rem !important;
    font-size: 0.9rem;
  }

  :deep(.p-datatable .p-datatable-tbody > tr > td) {
    padding: 0.75rem !important;
  }

  /* Better touch targets for tablets */
  :deep(.p-button) {
    min-height: 44px;
    min-width: 44px;
    padding: 0.75rem !important;
  }

  /* Optimize stats grid for tablets */
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  /* Adjust column widths for tablet */
  :deep(.p-datatable .p-datatable-thead > tr > th:nth-child(3)),
  :deep(.p-datatable .p-datatable-tbody > tr > td:nth-child(3)) {
    min-width: 180px !important;
  }
}

/* Mobile grid optimization - already in base styles above but add small tablet improvements */

/* ===== SMALL TABLET (576px+) ===== */
@media (min-width: 576px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  .stats-card {
    min-height: 48px;
    padding: 1rem !important;
  }

  /* Ensure all interactive elements meet 44px minimum */
  :deep(.p-button),
  :deep(.p-dropdown),
  :deep(.p-inputtext) {
    min-height: 44px;
    min-width: 44px;
  }

  /* Mobile card touch targets */
  .feed-card {
    min-height: 44px;
  }

  .feed-card-header {
    min-height: 44px;
    padding: 0.75rem !important;
  }

  /* Make DataTable responsive on mobile */
  :deep(.p-datatable-wrapper) {
    overflow-x: auto;
    width: 100%;
    max-width: 100vw;
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  }

  :deep(.p-datatable) {
    width: 100%;
    font-size: 0.85rem;
    table-layout: fixed; /* Use fixed table layout for better control */
  }

  /* Ensure card doesn't overflow */
  :deep(.p-card) {
    max-width: 100%;
    overflow: hidden;
  }

  :deep(.p-datatable .p-datatable-tbody > tr > td) {
    padding: 0.5rem 0.3rem;
  }

  :deep(.p-datatable .p-datatable-thead > tr > th) {
    padding: 0.5rem 0.3rem;
    font-size: 0.85rem;
  }

  /* Adjust column widths for mobile */
  :deep(.p-datatable .p-datatable-thead > tr > th[style*="min-width"]),
  :deep(.p-datatable .p-datatable-tbody > tr > td[style*="min-width"]) {
    min-width: auto !important;
    width: auto !important;
  }

  /* Optimize column widths for mobile */
  :deep(.p-datatable .p-datatable-thead > tr > th:nth-child(1)),
  :deep(.p-datatable .p-datatable-tbody > tr > td:nth-child(1)) {
    width: 2.5rem !important; /* Checkbox column */
  }

  :deep(.p-datatable .p-datatable-thead > tr > th:nth-child(2)),
  :deep(.p-datatable .p-datatable-tbody > tr > td:nth-child(2)) {
    width: 2.5rem !important; /* Index column */
  }

  :deep(.p-datatable .p-datatable-thead > tr > th:nth-child(3)),
  :deep(.p-datatable .p-datatable-tbody > tr > td:nth-child(3)) {
    width: 25% !important; /* Name column */
  }

  :deep(.p-datatable .p-datatable-thead > tr > th:nth-child(4)),
  :deep(.p-datatable .p-datatable-tbody > tr > td:nth-child(4)),
  :deep(.p-datatable .p-datatable-thead > tr > th:nth-child(5)),
  :deep(.p-datatable .p-datatable-tbody > tr > td:nth-child(5)) {
    width: 15% !important; /* Type and Security columns */
  }

  :deep(.p-datatable .p-datatable-thead > tr > th:nth-child(6)),
  :deep(.p-datatable .p-datatable-tbody > tr > td:nth-child(6)) {
    width: 25% !important; /* URL column */
  }

  /* Actions column - adapt to view mode */
  :deep(.p-datatable .p-datatable-thead > tr > th:nth-child(7)),
  :deep(.p-datatable .p-datatable-tbody > tr > td:nth-child(7)) {
    width: 15% !important; /* Actions column in detailed view */
  }

  /* Actions column in simplified view (becomes 5th column when Security & URL are hidden) */
  :deep(.p-datatable .p-datatable-thead > tr > th:nth-child(5):last-child),
  :deep(.p-datatable .p-datatable-tbody > tr > td:nth-child(5):last-child) {
    width: 25% !important; /* More space for actions in simplified view */
  }

  /* Style the DataTable header for mobile */
  :deep(.datatable-header) {
    padding: 0.5rem;
  }

  :deep(.search-container) {
    max-width: 100%;
  }

  :deep(.p-inputtext) {
    font-size: 0.85rem;
    padding: 0.4rem 0.5rem 0.4rem 2rem;
  }

  :deep(.p-input-icon-left > i) {
    left: 0.5rem;
  }

  /* Adjust header title and description for mobile */
  h1.text-xl {
    font-size: 1.125rem !important;
  }

  .subtitle-modern {
    font-size: 0.75rem !important;
  }
}

/* ===== LARGE DESKTOP (992px+) ===== */
@media (min-width: 992px) {
  .stats-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
  }

  h1.text-xl {
    font-size: 1.25rem !important;
  }

  .subtitle-modern {
    font-size: 0.875rem !important;
  }
}

/* Mobile Card Layout Styles */
.feed-cards {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.feed-card {
  background-color: white;
  transition: transform 0.2s, box-shadow 0.2s;
}

.feed-card:active {
  transform: translateY(1px);
}

.feed-card-header {
  border-bottom: 1px solid #f0f0f0;
  background-color: #f9fafb;
}

.feed-card-body {
  background-color: white;
}

.feed-card-footer {
  border-top: 1px solid #f0f0f0;
}

/* Provider and Security Icon Styles */
.provider-icon-wrapper,
.provider-icon-wrapper-mobile,
.security-icon-wrapper,
.security-icon-wrapper-mobile {
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.provider-icon-wrapper:hover,
.security-icon-wrapper:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.provider-icon-wrapper-mobile,
.security-icon-wrapper-mobile {
  width: 1.5rem !important;
  height: 1.5rem !important;
  border-radius: 0.375rem;
}

/* Truncate long text in cards */
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* Clean breadcrumb styling - only applied to elements with breadcrumb-clean class */

/* Secondary Navigation Tabs */
.secondary-nav-container {
  position: relative;
}

.nav-tabs-container {
  position: relative;
  overflow: hidden;
}

.nav-tab {
  cursor: pointer;
  border-bottom: 3px solid;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.nav-tab:hover {
  background-color: #f8fafc !important;
  transform: translateY(-1px);
}

.nav-tab-active {
  position: relative;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%) !important;
  border-color: #3b82f6 !important;
}

.nav-tab-active::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(147, 197, 253, 0.1));
  pointer-events: none;
}

.tab-indicator {
  position: absolute;
  bottom: -3px;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  animation: tabSlideIn 0.3s ease-out;
}

@keyframes tabSlideIn {
  from {
    width: 0;
    left: 50%;
  }
  to {
    width: 100%;
    left: 0;
  }
}

/* Mobile First - Base styles optimized for iPhone SE (375px) */
/* Single column on very small screens */
.stats-grid {
  grid-template-columns: 1fr;
  gap: 0.5rem;
}

.stats-card {
  min-height: 52px;
}

/* Larger touch targets for small screens */
:deep(.p-button) {
  min-height: 48px;
  min-width: 48px;
}

/* Further adjustments for very small screens */
:deep(.p-datatable .p-datatable-tbody > tr > td) {
  padding: 0.25rem 0.15rem;
  font-size: 0.75rem;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  padding: 0.25rem 0.15rem;
  font-size: 0.75rem;
}

/* Make buttons smaller on mobile */
:deep(.p-datatable .p-button.p-button-sm) {
  padding: 0.25rem;
  width: 2rem;
  height: 2rem;
}

/* Reduce tag size on mobile */
:deep(.p-tag) {
  padding: 0.15rem 0.3rem;
  font-size: 0.7rem;
}

/* Optimize URL display on mobile */
:deep(.p-datatable code) {
  font-size: 0.7rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
  display: inline-block;
}

/* More aggressive column optimization for very small screens */
:deep(.p-datatable .p-datatable-thead > tr > th:nth-child(3)),
:deep(.p-datatable .p-datatable-tbody > tr > td:nth-child(3)) {
  width: 30% !important; /* Increase name column */
}

:deep(.p-datatable .p-datatable-thead > tr > th:nth-child(4)),
:deep(.p-datatable .p-datatable-tbody > tr > td:nth-child(4)) {
  width: 20% !important; /* Increase type column */
}

/* Hide security column on very small screens only when it exists (detailed view) */
:deep(.p-datatable .p-datatable-thead > tr > th:nth-child(5):not(:last-child):not(:nth-last-child(2))),
:deep(.p-datatable .p-datatable-tbody > tr > td:nth-child(5):not(:last-child):not(:nth-last-child(2))) {
  width: 0 !important;
  padding: 0 !important;
  overflow: hidden;
  display: none;
}

:deep(.p-datatable .p-datatable-thead > tr > th:nth-child(6)),
:deep(.p-datatable .p-datatable-tbody > tr > td:nth-child(6)) {
  width: 30% !important; /* Increase URL column */
}

/* Ensure container has proper padding */
.px-2 {
  padding-left: 0.25rem !important;
  padding-right: 0.25rem !important;
}

/* Further optimize the DataTable header for very small screens */
:deep(.datatable-header) {
  padding: 0.3rem;
}

:deep(.p-inputtext) {
  font-size: 0.75rem;
  padding: 0.3rem 0.5rem 0.3rem 1.8rem;
}

:deep(.p-input-icon-left > i) {
  left: 0.4rem;
  font-size: 0.8rem;
}

/* Make the filter buttons even smaller */
:deep(.datatable-header .p-button.p-button-sm) {
  width: 1.8rem;
  height: 1.8rem;
  padding: 0.2rem;
}

:deep(.datatable-header .p-button-icon) {
  font-size: 0.8rem;
}

/* Mobile card specific styles */
.feed-card {
  margin-bottom: 0.5rem;
}

.feed-card-body {
  padding: 0.5rem;
}

/* Responsive tab styling */
.nav-tab {
  padding: 0.75rem 0.5rem !important;
  font-size: 0.875rem;
}

.secondary-nav-container {
  padding-left: 1rem !important;
  padding-right: 1rem !important;
}

/* ===== SMALL TABLET (576px+) ===== */
@media (min-width: 576px) {
  .nav-tab {
    padding: 1rem 0.75rem !important;
    font-size: 1rem;
  }

  .secondary-nav-container {
    padding-left: 1.25rem !important;
    padding-right: 1.25rem !important;
  }
}
</style>
